import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import AssetLibrary from "../../../core/assetloading/AssetLibrary";
import SoundType from "../../../core/sound/util/SoundType";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import TextTypeWriterAnimation from "../../../core/ui/animation/typewriters/TextTypeWriterAnimation";
import SkinKeyRegistry from "../../../core/ui/SkinKeyRegistry";
import ScenarioCommandEvent from "../../../ngt/scenario/controller/ScenarioCommandEvent";
import PlayerModel from "../../player/model/PlayerModel";
import SaveGameEvent from "../../savegame/event/SaveGameEvent";
import SaveGameModel from "../../savegame/model/SaveGameModel";
import PromiseUtil from "../../util/PromiseUtil";

export default class CoachIntroductionScreenMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {AssetLibrary} assetLibrary
     * @param {RevisableLanguagePack} languagePack 
     * @param {PlayerModel} playerModel
     * @param {SoundManager} soundManager
     * @param {SaveGameModel} saveGameModel
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, assetLibrary, languagePack, playerModel, soundManager, saveGameModel) {
        super(target, dispatcher, layoutManager, clickEaterFactory);
        this._assetLibrary = assetLibrary;
        this._languagePack = languagePack;
        this._playerModel = playerModel;
        this._soundManager = soundManager;
        this._saveGameModel = saveGameModel;

        this._currentTypeWriter = null;

        this._currentPage = 0;
        this._numberOfPages = this._countPages();
    }

    async initialize() {
        super.initialize();

        this._panel = this._target.panel;
        this._coach = this._target.coach;
        this._coach.initialX = this._coach.x;

        this._nextButton = this.createBracketButton(this._panel.continueButton, this._handleNextButton.bind(this), this._languagePack.getText(`coachIntroduction.continueButtonLabel`), SoundType.BRACKET_BUTTON_CLICK);
        this._backButton = this.createBracketButton(this._panel.backButton, this._handleBackButton.bind(this), this._languagePack.getText(`coachIntroduction.backButtonLabel`), SoundType.BRACKET_BUTTON_CLICK);
        this._closeButton = this.createBracketButton(this._panel.closeButton, this._handleCloseButton.bind(this), SoundType.BRACKET_BUTTON_CLICK);
        this._closeButton.setVisible(false);

        this._target.panel.contentTextField.text = "";
        this._backButton.setEnabled(false);

        // Start sequence.
        await this._animateIn(650); // with a delay to allow the screen to transition in first.

        this._updateCoachText();
    }

    _handleNextButton() {
        if (this._currentPage >= this._numberOfPages - 1) {
            this._finish();
            return;
        }

        this._currentPage++;
        this._updateCoachText();
    }

    _handleBackButton() {
        if (this._currentPage <= 0) {
            return;
        }

        this._currentPage--;
        this._updateCoachText();
    }

    _handleCloseButton() {
        this._finish();
    }

    async _finish() {
        this.disableInteraction();

        this._soundManager.stop();

        await this._animateOut();
        await PromiseUtil.delay(250);
        this.closeScreen(this._target);

        // Persist progress.
        this._saveGameModel.hasSeenIntroSequence = true;
        this.dispatch(SaveGameEvent.SAVE);

        // Play the first scenario.
        this.dispatch(ScenarioCommandEvent.PLAY_SCENARIO);
    }

    _updateCoachText() {
        const rawText = this._languagePack.getText(`coachIntroduction.coachIntro.steps.step${this._currentPage}`);
        const playerName = this._playerModel.getTrimmedPlayerName();
        const processedText = rawText.replace(/{{playerName}}/g, playerName);
        this._typeWriteText(processedText);

        // Disable back button on the first page
        if (this._currentPage == 0) {
            this._backButton.setEnabled(false);
        } else {
            this._backButton.setEnabled(true);
        }
    }

    async _typeWriteText(text) {
        this._typewriteSoundId = this._soundManager.playSound(SoundType.TYPEWRITER_COACH, null, null, 0, 0, -1);
        await new Promise(resolve => {
            this._target.panel.contentTextField.text = "";

            const duration = text.length * .007;
            this._currentTypeWriter?.stop();
            this._currentTypeWriter = TextTypeWriterAnimation.animate(this._target.panel.contentTextField, text, duration, 0, resolve);
        });
        this._soundManager.stopSound(this._typewriteSoundId, 50);
    }

    _countPages() {
        for (let i = 0; i < 100; i++) {
            const lpKey = `coachIntroduction.coachIntro.steps.step${i}`;
            const hasTextForStep = this._languagePack.hasText(lpKey);
            if (!hasTextForStep) {
                return i;
            }
        }

        throw new Error(`Pages out of range.`);
    }

    async _animateIn(delay = 0) {
        // this._coach.alpha = 0;

        this._panel.alpha = 0;
        this._panel.scale = .8;

        await PromiseUtil.delay(delay);

        // this._coach.gotoAndPlay(0);
        // await createjs.Tween.get(this._coach)
        //     .to({
        //         alpha: 1,
        //     }, 450, createjs.Ease.backOut)
        //     .toPromise();

        this._soundManager.playSound(SoundType.COACH_CHAT_PANEL_SHOW);
        await createjs.Tween.get(this._panel)
            .to({
                alpha: 1,
                scale: 1,
            }, 450, createjs.Ease.backOut)
            .toPromise();
    }

    async _animateOut() {
        this._soundManager.stopSound(this._typewriteSoundId, 50);

        this._soundManager.playSound(SoundType.COACH_CHAT_PANEL_HIDE);
        await createjs.Tween.get(this._panel)
            .to({
                alpha: 0,
                scale: .8,
            }, 450, createjs.Ease.backIn)
            .toPromise();

        await PromiseUtil.delay(250);

        this._soundManager.playSound(SoundType.COACH_HIDE);
        await createjs.Tween.get(this._coach)
            .to({
                x: this._coach.initialX + 200,
                alpha: 0,
            }, 450, createjs.Ease.backIn)
            .toPromise();
        await createjs.Tween.get(this._target.introBg)
            .to({
                alpha: 0,
            }, 450, createjs.Ease.sineIn)
            .toPromise();
    }
}
